<script setup lang="ts">
import { useRoute } from "vue-router";
import { ref, onMounted } from "vue";
import RvApi from "@/rvapi";
import type { Postcard } from "@/rvapi";
import { collectPaged } from "@/utils";

const route = useRoute();
const orderId = route.params.id as string;
const postcards = ref([] as Postcard[]);
const loaded = ref(false);
const timeout = ref(false);
const hasSuccessStatus = (status: string) => {
  return status === "SENT" || status === "PRINTED";
};
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const refresh = async (counter = 0) => {
  const searcher = async (skip: number, limit: number) => {
    return RvApi.searchGenericLookup("postcard/orderId", orderId, limit, skip);
  };
  const result = await collectPaged(searcher, 50);
  postcards.value = result as any;
  if (
    postcards.value.every((p) =>
      p.receivers.every((r) => hasSuccessStatus(r.status)),
    )
  ) {
    loaded.value = true;
  } else {
    if (counter < 300) {
      await sleep(1000);
      await refresh(counter + 1);
    } else {
      timeout.value = true;
    }
  }
};
onMounted(async () => {
  await refresh();
});
</script>

<template>
  <h1>Status</h1>
  <pn-toast
    closable="false"
    appearance="error"
    v-show="timeout"
    text="ERROR: timeout"
  ></pn-toast>

  <p>
    <a :href="`/order/${orderId}`">Order: {{ orderId }}</a>
  </p>
  <pn-table
    bordered="true"
    striped="true"
    v-for="p in postcards"
    :key="p.cardId"
  >
    <table>
      <thead>
        <tr>
          <th>Card #</th>
          <th>Receiver Id</th>
          <th colspan="2">Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in p.receivers" :key="r._id">
          <td>{{ p.postcardIndex }}</td>
          <td>{{ r._id }}</td>
          <td>{{ r.status }}</td>
          <td><pn-spinner v-show="!hasSuccessStatus(r.status)" /></td>
        </tr>
      </tbody>
    </table>
  </pn-table>
</template>

<style lang="scss" scoped>
$gray50: #f3f2f2;
.resend {
  text-align: end;
}
.postcard-details {
  display: grid;
  grid-template-columns: 1fr 1fr 3fr;
  grid-auto-flow: row dense;
  column-gap: 2rem;
}
.postcard {
  background-color: white;
  padding: 1rem;
  border-radius: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border: 1px solid $gray50;

  img {
    border: 1px dashed gray;
  }

  .receivers {
    grid-column: 1 / span 2;
  }
  .checkbox {
    padding: 0px;
    label {
      margin: auto;
      padding: 15px;
      display: block;
    }
  }
}

div.grid-two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
}

.full-width {
  max-width: 300px;
  max-height: 300px;
}

.rounded {
  border-radius: 1rem;
}
</style>
